import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2 } from "lucide-react";
import { useAdminAuth } from "./AuthContext";
import { useToast } from "../hooks/use-toast";

const STATUS_OPTIONS = ["requested", "confirmed"];

const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/**
 * Unified "create" modal used by:
 *  - "Add Customer" (lockCustomer=false) — admin enters customer fields + first job
 *  - "Add Job" (lockCustomer=true, customer=...) — admin only enters job fields
 *
 * Always silent (no SMS to customer / business) since admin is creating it manually.
 */
export default function JobCreateModal({
  open,
  onOpenChange,
  customer = null,
  lockCustomer = false,
  onCreated,
}) {
  const { authAxios } = useAdminAuth();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    if (!open) return;
    setForm({
      name: customer?.name || "",
      phone: customer?.phone || "",
      email: customer?.email || "",
      address: customer?.address || "",
      suburb: customer?.suburb || "",
      service_title: "",
      notes: "",
      date: today(),
      time: "09:00",
      time_to: "",
      initial_status: "confirmed",
    });
  }, [open, customer]);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    // Basic validation
    const req = ["name", "phone", "address", "suburb", "service_title", "date", "time"];
    const missing = req.filter((k) => !(form[k] || "").trim());
    if (missing.length) {
      toast({
        title: "Missing fields",
        description: missing.join(", "),
        variant: "destructive",
      });
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: (form.email || "").trim(),
        address: form.address.trim(),
        suburb: form.suburb.trim(),
        service: form.service_title.trim().toLowerCase().replace(/\s+/g, "_"),
        service_title: form.service_title.trim(),
        date: form.date,
        time: form.time,
        time_to: form.time_to || "",
        notes: (form.notes || "").trim(),
        booking_type: "service",
        silent: true,
        initial_status: form.initial_status || "confirmed",
      };
      const r = await authAxios.post("/bookings", payload);
      toast({ title: lockCustomer ? "Job added" : "Customer & job added" });
      onCreated?.(r.data);
      onOpenChange(false);
    } catch (e) {
      toast({
        title: "Create failed",
        description: e?.response?.data?.detail || "Server error",
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="job-create-modal">
        <DialogHeader>
          <DialogTitle>
            {lockCustomer ? "Add job" : "Add customer & first job"}
          </DialogTitle>
          <DialogDescription>
            {lockCustomer
              ? `New job for ${customer?.name || "this customer"}. No SMS sent.`
              : "Create a customer record with their first job. No SMS sent."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <section>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Customer
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name *">
                <Input
                  value={form.name || ""}
                  onChange={(e) => update("name", e.target.value)}
                  disabled={lockCustomer}
                  data-testid="create-name"
                />
              </Field>
              <Field label="Phone *">
                <Input
                  value={form.phone || ""}
                  onChange={(e) => update("phone", e.target.value)}
                  disabled={lockCustomer}
                  placeholder="0400 123 456"
                  data-testid="create-phone"
                />
              </Field>
              <Field label="Email" colSpan={2}>
                <Input
                  type="email"
                  value={form.email || ""}
                  onChange={(e) => update("email", e.target.value)}
                  disabled={lockCustomer}
                  data-testid="create-email"
                />
              </Field>
              <Field label="Address *">
                <Input
                  value={form.address || ""}
                  onChange={(e) => update("address", e.target.value)}
                  disabled={lockCustomer}
                  data-testid="create-address"
                />
              </Field>
              <Field label="Suburb *">
                <Input
                  value={form.suburb || ""}
                  onChange={(e) => update("suburb", e.target.value)}
                  disabled={lockCustomer}
                  data-testid="create-suburb"
                />
              </Field>
            </div>
          </section>

          <section>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">
              Job
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Service *" colSpan={2}>
                <Input
                  value={form.service_title || ""}
                  onChange={(e) => update("service_title", e.target.value)}
                  placeholder="e.g. Hot water repair"
                  data-testid="create-service"
                />
              </Field>
              <Field label="Date *">
                <Input
                  type="date"
                  value={form.date || ""}
                  onChange={(e) => update("date", e.target.value)}
                  data-testid="create-date"
                />
              </Field>
              <Field label="Initial status">
                <Select
                  value={form.initial_status || "confirmed"}
                  onValueChange={(v) => update("initial_status", v)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
              <Field label="From *">
                <Input
                  type="time"
                  value={form.time || ""}
                  onChange={(e) => update("time", e.target.value)}
                  data-testid="create-time"
                />
              </Field>
              <Field label="To">
                <Input
                  type="time"
                  value={form.time_to || ""}
                  onChange={(e) => update("time_to", e.target.value)}
                  data-testid="create-time-to"
                />
              </Field>
              <Field label="Notes" colSpan={2}>
                <Textarea
                  rows={3}
                  value={form.notes || ""}
                  onChange={(e) => update("notes", e.target.value)}
                  data-testid="create-notes"
                />
              </Field>
            </div>
          </section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={submitting}
            className="bg-sky-600 hover:bg-sky-700 text-white"
            data-testid="create-submit-btn"
          >
            {submitting && <Loader2 className="animate-spin mr-2" size={14} />}
            {lockCustomer ? "Add job" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, colSpan = 1, children }) {
  return (
    <div className={colSpan === 2 ? "col-span-2" : ""}>
      <Label className="text-xs text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
